import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ProgrammeEnquiryModal from "@/components/ProgrammeEnquiryModal";
import {
  ArrowRight,
  ArrowLeft,
  Users,
  Beaker,
  Landmark,
  Target,
  Sparkles,
  Handshake,
  Layers,
  ClipboardCheck,
  ChevronRight,
  Linkedin,
} from "lucide-react";

/**
 * Operon-powered Programmes
 * A separate business-unit page: same brand DNA, deeper navy + cyan accent
 * for a distinct feel from the main Operon Grow experience.
 */
export default function Programmes() {
  const navigate = useNavigate();
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formats = [
    {
      no: "01",
      title: "Company Strategy Day",
      spec: "One company · One day",
      description:
        "Focused deep-dive with UK experts on a priority strategic challenge.",
      output: "Strategic clarity and next steps.",
    },
    {
      no: "02",
      title: "Selective Strategy Sprint",
      spec: "Two to four companies · Two days",
      description:
        "Two days of strategy sessions plus curated introductions to UK investors and partners.",
      output: "Company roadmaps + UK ecosystem connections.",
    },
    {
      no: "03",
      title: "Portfolio Strategy Retreat",
      spec: "Five to eight companies · Four days",
      description:
        "Immersive strategy retreat combined with access to UK pharma, investors, clinicians and innovation hubs.",
      output: "Full strategic reviews + UK ecosystem immersion.",
    },
  ];

  const ecosystem = [
    { icon: Landmark, title: "Top-tier investors", detail: "The most active therapeutic funds in Europe." },
    { icon: Beaker, title: "Global pharma leaders", detail: "AstraZeneca, GSK, J&J, Roche and more." },
    { icon: Users, title: "Academic & research excellence", detail: "World-leading universities and research institutes." },
    { icon: Sparkles, title: "Innovation hubs & incubators", detail: "The Golden Triangle's densest cluster of therapeutic hubs." },
    { icon: ClipboardCheck, title: "Regulatory & market access", detail: "MHRA, NICE and NHS pathways in one place." },
    { icon: Handshake, title: "Founder-friendly environment", detail: "A collaborative culture built for early-stage teams." },
  ];

  const topics = [
    {
      title: "Market, Competitors & Positioning",
      body: "Target market, value chain, competitive landscape, positioning, partners and customers.",
    },
    {
      title: "Regulatory & Clinical",
      body: "Indication strategy, TPP, clinical development plan, regulatory route and evidence strategy.",
    },
    {
      title: "IP & Business Model",
      body: "Technology value, IP strategy, defensibility, business model and partnering options.",
    },
    {
      title: "Milestones & Fundraising",
      body: "Value inflection points, team & capability gaps, funding strategy and investor positioning.",
    },
  ];

  const inclusions = [
    {
      n: "1",
      title: "Operon AI-platform",
      body: "Strategy diagnostics, guidance and efficient execution.",
    },
    {
      n: "2",
      title: "Carefully selected experts",
      body: "From pharma, investors, clinicians and operators.",
    },
    {
      n: "3",
      title: "Structured challenge & peer learning",
      body: "Rigorous sessions that pressure-test your thinking together.",
    },
    {
      n: "4",
      title: "Clear outputs & prioritised action plans",
      body: "You leave with decisions, not just discussion.",
    },
    {
      n: "5",
      title: "Post-programme follow-up",
      body: "Continued support through the Operon platform.",
    },
  ];

  const scrollToFormats = () => {
    const el = document.getElementById("formats");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#060B1C] text-[#E9EEF9] font-sans overflow-x-hidden">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute top-[-10%] left-[-10%] w-[50rem] h-[50rem] rounded-full bg-[#3E7AD6]/10 blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[45rem] h-[45rem] rounded-full bg-[#57C7E6]/10 blur-[120px]" />
      </div>

      {/* Top Bar */}
      <nav className="border-b border-[#243768]/50 bg-[#0B1735]/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            data-testid="btn-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Operon</span>
          </button>
          <div className="hidden md:flex items-center gap-2 text-[11px] tracking-[0.25em] font-mono text-[#9DB0D6] uppercase">
            Activate&nbsp;the&nbsp;right&nbsp;pathways
          </div>
          <Button
            onClick={() => setEnquiryOpen(true)}
            className="bg-[#57C7E6] hover:bg-[#3DA9C9] text-[#060B1C] font-semibold rounded-full px-5 py-2 h-auto"
            data-testid="btn-design-programme"
          >
            Design my programme
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-24">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight mb-6">
              Change your gear.
              <br />
              <span className="text-[#57C7E6]">Discover the UK.</span>
              <br />
              <span className="text-white/95">Accelerate your therapeutic ventures.</span>
            </h1>
            <p className="text-lg text-[#9DB0D6] leading-relaxed max-w-2xl mb-4">
              For accelerators, delegations and investor portfolios, our strategic programmes improve the clarity, commercial readiness and funding prospects of your therapeutic startups, with direct access to the UK&apos;s world-class ecosystem.
            </p>
            <p className="text-base text-[#9DB0D6]/80 leading-relaxed max-w-2xl mb-8">
              We combine expert-led strategy support with access to the people, companies and institutions that make the UK a leading therapeutic innovation hub.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setEnquiryOpen(true)}
                className="bg-[#57C7E6] hover:bg-[#3DA9C9] text-[#060B1C] font-semibold rounded-full px-6 py-6 h-auto text-base"
                data-testid="btn-hero-design"
              >
                Design my programme
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={scrollToFormats}
                variant="outline"
                className="border-[#57C7E6]/40 text-[#57C7E6] hover:bg-[#57C7E6]/10 hover:text-white rounded-full px-6 py-6 h-auto text-base bg-transparent"
                data-testid="btn-hero-formats"
              >
                See the three formats
              </Button>
            </div>
          </div>

          {/* Earth globe with UK highlighted */}
          <div className="relative aspect-square max-w-md mx-auto lg:mx-0 w-full" data-testid="hero-globe">
            {/* Ambient glow */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#3E7AD6] via-[#4E8DEB] to-[#57C7E6] opacity-30 blur-3xl animate-pulse" />
            {/* Globe */}
            <div className="relative w-full h-full rounded-full border border-[#57C7E6]/30 bg-gradient-to-br from-[#0B1735] to-[#14264F] overflow-hidden shadow-2xl shadow-[#57C7E6]/10">
              {/* Meridian rings */}
              <div className="absolute inset-4 rounded-full border border-[#243768]/60" />
              <div className="absolute inset-10 rounded-full border border-[#243768]/40" />
              <div className="absolute inset-16 rounded-full border border-[#243768]/20" />

              {/* World map — clipped to the globe circle */}
              <div className="absolute inset-[10%] rounded-full overflow-hidden">
                <img
                  src="/world-map.svg"
                  alt="World map"
                  className="absolute select-none pointer-events-none max-w-none"
                  style={{
                    // Keep native aspect ratio (950x620) — only set width, let height auto.
                    // Positioned so the UK sits at roughly (55%, 32%) of the visible globe.
                    width: "220%",
                    height: "auto",
                    left: "-54%",
                    top: "8%",
                    filter: "brightness(0) invert(1) opacity(0.28)",
                  }}
                  aria-hidden="true"
                />
              </div>

              {/* UK highlight marker (aligned to the "britain" path in the SVG at x≈460, y≈160 of the 950x620 viewBox) */}
              <div
                className="absolute"
                style={{ left: "50.5%", top: "46%" }}
                data-testid="uk-marker"
              >
                {/* Outer pulsing ring */}
                <span className="absolute -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#57C7E6]/20 animate-ping" />
                <span className="absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#57C7E6]/40" />
                <span className="absolute -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#57C7E6] shadow-[0_0_20px_#57C7E6]" />
                {/* Callout line + label */}
                <div className="absolute left-3 -top-4 flex items-center gap-2 whitespace-nowrap">
                  <span className="block w-8 h-px bg-[#57C7E6]/60" />
                  <span className="text-[10px] font-mono tracking-[0.25em] text-[#57C7E6] uppercase">
                    United Kingdom
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTER 01 - FORMATS */}
      <section id="formats" className="relative border-t border-[#243768]/50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-14">
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
              Three formats.
            </h2>
            <p className="text-[#9DB0D6] text-lg leading-relaxed">
              Pick the depth that fits your delegation — each step scales the number of companies, the days on the ground and the ecosystem access.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {formats.map((f, idx) => (
              <div
                key={f.no}
                className="group relative rounded-2xl border border-[#243768] bg-gradient-to-br from-[#0B1735] to-[#0F1D40] p-7 hover:border-[#57C7E6]/50 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#57C7E6]/10"
                data-testid={`format-card-${idx + 1}`}
              >
                {/* tier indicator bars */}
                <div className="flex gap-1 mb-6">
                  {[1, 2, 3].map((n) => (
                    <div
                      key={n}
                      className={`h-1 flex-1 rounded-full ${
                        n <= idx + 1 ? "bg-[#57C7E6]" : "bg-[#243768]"
                      }`}
                    />
                  ))}
                </div>
                <div className="text-[10px] font-mono text-[#6C7EA6] tracking-[0.3em] mb-3">
                  FORMAT {f.no}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2 leading-tight">
                  {f.title}
                </h3>
                <div className="text-xs font-mono text-[#57C7E6] mb-5 pb-5 border-b border-[#243768]/60">
                  {f.spec}
                </div>
                <p className="text-[#9DB0D6] text-sm leading-relaxed mb-6">
                  {f.description}
                </p>
                <div className="rounded-xl bg-[#060B1C]/60 border border-[#243768]/60 p-4">
                  <div className="text-[10px] font-mono text-[#6C7EA6] tracking-[0.2em] uppercase mb-1">
                    Outputs
                  </div>
                  <div className="text-white text-sm font-medium">
                    {f.output}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHAPTER 02 - ECOSYSTEM */}
      <section className="relative border-t border-[#243768]/50 py-20 md:py-28 bg-[#0B1735]/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-14">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-6">
                The UK advantage.
              </h2>
              <p className="text-[#9DB0D6] text-lg leading-relaxed">
                What makes the UK a leading therapeutic innovation hub — opened up to your portfolio.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {ecosystem.map((e) => {
                const Icon = e.icon;
                return (
                  <div
                    key={e.title}
                    className="rounded-2xl border border-[#243768] bg-[#0F1D40]/60 p-5 hover:border-[#57C7E6]/40 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#57C7E6]/10 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-[#57C7E6]" />
                    </div>
                    <div className="text-white font-semibold mb-1">{e.title}</div>
                    <div className="text-[#9DB0D6] text-xs">{e.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTER 03 - TOPICS */}
      <section className="relative border-t border-[#243768]/50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-14">
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              Core strategic topics covered.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {topics.map((t, i) => (
              <div
                key={t.title}
                className="group rounded-2xl border border-[#243768] bg-gradient-to-br from-[#0B1735]/60 to-[#0F1D40]/60 p-7 hover:border-[#57C7E6]/40 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="text-4xl font-extrabold text-[#57C7E6]/30 font-mono leading-none">
                    0{i + 1}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{t.title}</h3>
                    <p className="text-[#9DB0D6] text-sm leading-relaxed">{t.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHAPTER 04 - INCLUSIONS */}
      <section className="relative border-t border-[#243768]/50 py-20 md:py-28 bg-[#0B1735]/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-14">
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              What&apos;s included in each format.
            </h2>
          </div>

          <div className="space-y-3">
            {inclusions.map((s) => (
              <div
                key={s.n}
                className="flex items-start gap-6 rounded-2xl border border-[#243768] bg-[#0F1D40]/40 p-6 hover:border-[#57C7E6]/40 transition-colors"
              >
                <div className="shrink-0 w-12 h-12 rounded-full border border-[#57C7E6]/40 bg-[#57C7E6]/5 flex items-center justify-center text-[#57C7E6] font-mono font-semibold">
                  {s.n}
                </div>
                <div className="flex-1">
                  <div className="text-white font-semibold text-lg mb-1">{s.title}</div>
                  <div className="text-[#9DB0D6] text-sm">{s.body}</div>
                </div>
                <ChevronRight className="hidden md:block w-5 h-5 text-[#6C7EA6] mt-3" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT MARCO */}
      <section className="relative border-t border-[#243768]/50 py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-3xl border border-[#243768] bg-gradient-to-br from-[#0F1D40] to-[#14264F] p-8 md:p-12">
            <div className="grid md:grid-cols-[220px_1fr] gap-8 md:gap-10 items-center">
              <div className="mx-auto md:mx-0">
                <div className="relative w-44 h-44 md:w-52 md:h-52 rounded-full overflow-hidden border-2 border-[#57C7E6]/40 shadow-2xl shadow-[#57C7E6]/10">
                  <img
                    src="/marco-delise.png"
                    alt="Marco Delise, PhD"
                    className="w-full h-full object-cover"
                    data-testid="img-marco"
                  />
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono text-[#57C7E6] tracking-[0.3em] uppercase mb-3">
                  Who leads it
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Marco Delise, PhD
                </h3>
                <p className="text-[#9DB0D6] text-lg leading-relaxed max-w-2xl mb-6">
                  Engineer, scientist and former founder, Marco supports startups in the main therapeutics cluster in the UK and has designed, organized and led 4 accelerator programmes.
                </p>
                <a
                  href="https://www.linkedin.com/in/marco-delise"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="btn-marco-linkedin"
                  className="inline-flex items-center gap-2 rounded-full border border-[#57C7E6]/40 bg-[#57C7E6]/10 hover:bg-[#57C7E6]/20 hover:border-[#57C7E6] text-white font-medium px-5 py-2.5 text-sm transition-colors"
                >
                  <Linkedin className="w-4 h-4 text-[#57C7E6]" />
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative border-t border-[#243768]/50 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="text-[10px] font-mono text-[#57C7E6] tracking-[0.3em] uppercase mb-6">
            Strategy · Connections · Insight
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight mb-6">
            Strategy. <span className="text-[#57C7E6]">Connections.</span> Insight.
          </h2>
          <p className="text-[#9DB0D6] text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Your gateway to the UK therapeutics ecosystem. Let&apos;s design the right programme for you.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              onClick={() => setEnquiryOpen(true)}
              className="bg-[#57C7E6] hover:bg-[#3DA9C9] text-[#060B1C] font-semibold rounded-full px-8 py-6 h-auto text-base"
              data-testid="btn-final-design"
            >
              Let&apos;s design the right programme
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#243768]/50 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo-complete.png" alt="Operon" className="h-12 object-contain" />
          </div>
          <div className="text-[10px] font-mono text-[#6C7EA6] tracking-[0.3em] uppercase">
            Activate the right pathways
          </div>
        </div>
      </footer>

      <ProgrammeEnquiryModal open={enquiryOpen} onOpenChange={setEnquiryOpen} />
    </div>
  );
}
